import { useEffect } from "react";

import { RumActionName, rumActionNames } from "../../../shared/monitoring/rum-action-names";
import { useMonitor } from "../../../shared/monitoring/use-monitor";

export type NetworkRole = "buyer" | "supplier";

// A lookup rather than a template, so the taxonomy stays the single list of names the app tracks.
const NETWORK_AREA_ACTIONS: Record<NetworkRole, RumActionName> = {
  buyer: rumActionNames.networkBuyerArea,
  supplier: rumActionNames.networkSupplierArea,
};

/**
 * Tracks in RUM that the Network area rendered for a given role, so the Datadog Network dashboard
 * can group usage by account without a hardcoded account list.
 *
 * The render is the signal: these components only mount once their capability guard passed, so the
 * action cannot be emitted for an account that lacks the role. That is what a URL filter can't do —
 * RUM records a view before a guard redirects, so `/network/*` view events exist for users who
 * were bounced and never saw the page.
 *
 * `enabled` is a parameter rather than read from `useNetworkCapabilities()` here so each call site
 * tracks only the role its route actually serves. Reading both flags internally would make a buyer
 * who bounces off a supplier URL emit a buyer action from a component that rendered nothing for
 * them.
 *
 * Fires once per mount, so a session that visits the landing and then a subtree emits twice. The
 * dashboard widget counts distinct `@session.id`, which collapses the duplicates.
 */
export function useNetworkRoleUserTracking(role: NetworkRole, enabled: boolean): void {
  const monitor = useMonitor();

  useEffect(() => {
    if (enabled) {
      monitor.action(NETWORK_AREA_ACTIONS[role]);
    }
  }, [role, enabled, monitor]);
}
